// @givmo/org-console — the organization QR design's transport (FCM-WEB4 / PE-WEB11; plan §FC-14).
//
// Every call is a GENERATED op over `/org/{org_ref}/qr-design…` and the fundraiser's supplied-design
// render — the snapshot carries them, so nothing here is hand-written. Five ops, three purposes:
//
//   read the default     GET  /org/{org_ref}/qr-design                          (any administrator)
//   save the default     PUT  /org/{org_ref}/qr-design                          (MANAGE_ORG_QR)
//   render a WORKING     POST …/fundraisers/{fundraiser_id}/share/qr            (the fundraiser's code)
//   design, saving none  POST /org/{org_ref}/qr-design/public-qr                (the public code)
//   render the SAVED     GET  /org/{org_ref}/qr-design/public-qr?format=        (MANAGE_ORG_QR)
//   public code
//
// ⚠ A RENDER CARRIES A DESIGN, NEVER A LINK. What each code encodes is the server's own (the
// fundraiser's canonical organization link; the organization's public target); the request sends
// only the spec and the format.
//
// ⚠ EVERY PREVIEW IS A `data:` URL. `img-src` admits `data:` on every console host, while `blob:`
// is admitted on one only — and the SVG is drawn in an <img>, never inlined, so it runs nothing.

import type { UseQueryOptions } from '@tanstack/react-query';
import { isGivmoError, readBinaryMeta, sdk, type QrDesignSpec } from '@givmo/api-client';
import { orgQrDesignGetOrgQrDesignQueryKey } from '@givmo/api-client/query';
import { decodeOrgQrDesign, qrDesignKey, type OrgQrDesignRead } from './org-qr-design-model';

/** Which code a render draws: one fundraiser's share code, or the organization's public code. */
export type OrgQrTarget = { kind: 'fundraiser'; fundraiserId: string } | { kind: 'public' };

export type OrgQrFormat = 'svg' | 'png' | 'pdf';
export const ORG_QR_FORMATS: readonly OrgQrFormat[] = ['svg', 'png', 'pdf'];

/** A read that answered with a status — no retry. Anything else keeps the hosts' one retry. */
function answeredRetry(failureCount: number, error: Error): boolean {
  return (
    !(isGivmoError(error) && error.status !== undefined && error.status < 500) && failureCount < 1
  );
}

export function orgQrDesignOptions(orgRef: string): UseQueryOptions<OrgQrDesignRead, Error> {
  const options = { path: { org_ref: orgRef } };
  return {
    queryKey: orgQrDesignGetOrgQrDesignQueryKey(options),
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const { data } = await sdk.orgQrDesignGetOrgQrDesign({
        ...options,
        signal,
        throwOnError: true,
      });
      return decodeOrgQrDesign(data);
    },
    retry: answeredRetry,
  };
}

/** The default's cache key — a save writes its answer here. */
export function orgQrDesignKey(orgRef: string) {
  return orgQrDesignGetOrgQrDesignQueryKey({ path: { org_ref: orgRef } });
}

/** Replace the organization's ONE default. The answer is the read's shape. */
export async function saveOrgQrDesign(
  orgRef: string,
  spec: QrDesignSpec,
): Promise<OrgQrDesignRead> {
  const { data } = await sdk.orgQrDesignUpdateOrgQrDesign({
    path: { org_ref: orgRef },
    body: spec,
    throwOnError: true,
  });
  return decodeOrgQrDesign(data);
}

/** The server's SVG as an image source. */
export function svgDataUrl(svg: string): string {
  const bytes = new TextEncoder().encode(svg);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return `data:image/svg+xml;base64,${btoa(binary)}`;
}

type RenderOptions = { signal?: AbortSignal };

function renderSupplied(
  orgRef: string,
  target: OrgQrTarget,
  spec: QrDesignSpec,
  format: OrgQrFormat,
  parse: { parseAs: 'text' } | { parseAs: 'blob'; responseStyle: 'fields' },
  options: RenderOptions = {},
) {
  const body = { spec, format };
  return target.kind === 'fundraiser'
    ? sdk.orgFundraisersRenderSuppliedOrgFundraiserShareQr({
        path: { org_ref: orgRef, fundraiser_id: target.fundraiserId },
        body,
        ...parse,
        ...options,
      })
    : sdk.orgQrDesignRenderSuppliedOrgPublicQr({
        path: { org_ref: orgRef },
        body,
        ...parse,
        ...options,
      });
}

/** The WORKING design drawn as the target's code — the editor's preview. Saves nothing. */
export function orgQrPreviewOptions(
  orgRef: string,
  target: OrgQrTarget,
  spec: QrDesignSpec,
): UseQueryOptions<string, Error> {
  const targetKey = target.kind === 'fundraiser' ? `fundraiser:${target.fundraiserId}` : 'public';
  return {
    queryKey: ['org-qr-preview', orgRef, targetKey, qrDesignKey(spec)] as const,
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const result = await renderSupplied(
        orgRef,
        target,
        spec,
        'svg',
        { parseAs: 'text' },
        { signal },
      );
      if (result.error) throw result.error;
      return svgDataUrl(String(result.data ?? ''));
    },
    // A design renders the same bytes every time, so flipping back to one reuses its render — the
    // preview is rate-limited per person, and a cached answer spends none of it.
    staleTime: Infinity,
    retry: false,
  };
}

/** The organization's public code in its SAVED default — the Profile page's card. */
export function orgPublicQrOptions(orgRef: string): UseQueryOptions<string, Error> {
  return {
    queryKey: orgPublicQrKey(orgRef),
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const { data } = await sdk.orgQrDesignRenderOrgPublicQr({
        path: { org_ref: orgRef },
        query: { format: 'svg' },
        parseAs: 'text',
        signal,
        throwOnError: true,
      });
      return svgDataUrl(String(data ?? ''));
    },
    retry: answeredRetry,
  };
}

/** The public card's cache key — a save of the default invalidates it. */
export function orgPublicQrKey(orgRef: string) {
  return ['org-public-qr', orgRef] as const;
}

/** Hand `blob` to the browser as a download named `filename`. */
export function saveQrFile(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function fallbackName(target: OrgQrTarget | 'saved-public', format: OrgQrFormat): string {
  const stamp = new Date().toISOString().slice(0, 10);
  const subject =
    typeof target === 'object' && target.kind === 'fundraiser' ? 'fundraiser_qr' : 'qr';
  return `givmo_${subject}_${stamp}.${format}`;
}

function blobOf(data: unknown): Blob {
  return data instanceof Blob
    ? data
    : new Blob([(data ?? '') as BlobPart], { type: 'application/octet-stream' });
}

/** Download the WORKING design as the target's code. Saves no design. Throws the server's refusal. */
export async function downloadSuppliedQr(
  orgRef: string,
  target: OrgQrTarget,
  spec: QrDesignSpec,
  format: OrgQrFormat,
): Promise<void> {
  const result = await renderSupplied(orgRef, target, spec, format, {
    parseAs: 'blob',
    responseStyle: 'fields',
  });
  if (result.error) throw result.error;
  if (!result.response) throw new Error('The QR code download failed.');
  const name = readBinaryMeta(result.response).filename ?? fallbackName(target, format);
  saveQrFile(blobOf(result.data), name);
}

/** Download the public code in the SAVED default. Throws the server's refusal. */
export async function downloadPublicQr(orgRef: string, format: OrgQrFormat): Promise<void> {
  const result = await sdk.orgQrDesignRenderOrgPublicQr({
    path: { org_ref: orgRef },
    query: { format },
    parseAs: 'blob',
    responseStyle: 'fields',
  });
  if (result.error) throw result.error;
  if (!result.response) throw new Error('The QR code download failed.');
  const name = readBinaryMeta(result.response).filename ?? fallbackName('saved-public', format);
  saveQrFile(blobOf(result.data), name);
}
